/**
 * PreviewModal - Preview AI-parsed entry before confirming
 */

import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  X,
  Check,
  Edit2,
  Calendar,
  Wallet,
  Tag,
  Building2,
  RotateCcw,
  TrendingUp,
  TrendingDown,
  MessageSquare,
} from 'lucide-react';
import { useCashflowStore } from '../../stores/cashflowStore';

// Format currency with proper locale
const formatCurrency = (amount, currency = 'TRY') => {
  const formatter = new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

// Day names in Turkish
const DAY_NAMES = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

// Get Turkish suffix for day numbers
const getDaySuffix = (day) => {
  if (!day) return '';
  // Turkish number suffixes based on vowel harmony
  const lastDigit = day % 10;
  const isTeen = day >= 10 && day < 20;

  if (isTeen) return "'inde";
  if (lastDigit === 1 || lastDigit === 2 || lastDigit === 5 || lastDigit === 7 || lastDigit === 8) return "'inde";
  if (lastDigit === 3 || lastDigit === 4) return "'ünde";
  if (lastDigit === 6 || lastDigit === 9 || lastDigit === 0) return "'ında";
  return "'inde";
};

// Get recurring description
const getRecurringDescription = (rule) => {
  if (!rule) return null;

  const dayName = rule.dayOfWeek !== undefined ? DAY_NAMES[rule.dayOfWeek] : null;

  switch (rule.frequency) {
    case 'weekly':
      return dayName ? `Her hafta ${dayName}` : 'Her hafta';
    case 'biweekly':
      return dayName ? `Her 2 haftada bir ${dayName}` : 'Her 2 haftada bir';
    case 'monthly':
      // Check for last day of month
      if (rule.isLastDayOfMonth) {
        return 'Her ay sonunda';
      }
      // Check for day of week pattern (e.g., first Friday)
      if (dayName && rule.weekOfMonth) {
        const weekLabels = ['İlk', 'İkinci', 'Üçüncü', 'Dördüncü', 'Son'];
        return `Her ayın ${weekLabels[rule.weekOfMonth - 1] || ''} ${dayName}'si`;
      }
      // Check for simple day of week pattern
      if (dayName) {
        return `Her ayın ilk ${dayName}'si`;
      }
      // Day of month
      if (rule.dayOfMonth) {
        return `Her ayın ${rule.dayOfMonth}${getDaySuffix(rule.dayOfMonth)}`;
      }
      return 'Her ay';
    case 'yearly':
      if (rule.isLastDayOfMonth) {
        return 'Her yıl ay sonunda';
      }
      return 'Her yıl';
    default:
      return null;
  }
};

// Category labels in Turkish
const CATEGORY_LABELS = {
  salary: 'Maaş',
  rent: 'Kira',
  credit_card: 'Kredi Kartı',
  loan: 'Kredi/Taksit',
  subscription: 'Abonelik',
  bill: 'Fatura',
  transfer: 'Transfer',
  investment: 'Yatırım',
  shopping: 'Alışveriş',
  food: 'Yemek',
  transport: 'Ulaşım',
  health: 'Sağlık',
  entertainment: 'Eğlence',
  other: 'Diğer',
};

// Account type labels
const ACCOUNT_TYPE_LABELS = {
  bank: 'Banka Hesabı',
  credit_card: 'Kredi Kartı',
  cash: 'Nakit',
  crypto: 'Kripto',
  other: 'Diğer',
};

const PreviewModal = () => {
  const {
    parsedEntry,
    closePreviewModal,
    confirmParsedEntry,
    editParsedEntry,
  } = useCashflowStore();

  if (!parsedEntry) return null;

  const isIncome = parsedEntry.type === 'income';
  const recurringDesc = getRecurringDescription(parsedEntry.recurring_rule);

  // Format the date
  let formattedDate = 'Tarih belirtilmedi';
  try {
    if (parsedEntry.date) {
      formattedDate = format(new Date(parsedEntry.date), 'd MMMM yyyy', { locale: tr });
    }
  } catch (e) {
    formattedDate = parsedEntry.date;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-xl shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${isIncome ? 'bg-emerald-500/20' : 'bg-red-500/20'} rounded-lg flex items-center justify-center`}>
              {isIncome ? (
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-400" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Giriş Önizleme</h2>
              <p className="text-sm text-gray-400">Bilgileri kontrol edin</p>
            </div>
          </div>
          <button
            onClick={closePreviewModal}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Main info */}
          <div className="space-y-3">
            {/* Title & Amount */}
            <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
              <div>
                <p className="text-sm text-gray-400">Başlık</p>
                <p className="text-white font-medium">{parsedEntry.title}</p>
              </div>
              <p className={`text-xl font-bold ${isIncome ? 'text-emerald-400' : 'text-red-400'}`}>
                {isIncome ? '+' : '-'}{formatCurrency(parsedEntry.amount, parsedEntry.currency)}
              </p>
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Category */}
              <div className="p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-400 mb-1">
                  <Tag className="w-4 h-4" />
                  <span className="text-xs">Kategori</span>
                </div>
                <p className="text-white text-sm">
                  {CATEGORY_LABELS[parsedEntry.category] || parsedEntry.category || 'Belirtilmedi'}
                </p>
              </div>

              {/* Date */}
              <div className="p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-400 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs">Tarih</span>
                </div>
                <p className="text-white text-sm">{formattedDate}</p>
              </div>

              {/* Account */}
              {parsedEntry.account_name && (
                <div className="p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <Building2 className="w-4 h-4" />
                    <span className="text-xs">Hesap</span>
                  </div>
                  <p className="text-white text-sm">{parsedEntry.account_name}</p>
                  {parsedEntry.account_type && (
                    <p className="text-xs text-gray-500">
                      {ACCOUNT_TYPE_LABELS[parsedEntry.account_type] || parsedEntry.account_type}
                    </p>
                  )}
                </div>
              )}

              {/* Recurring */}
              {parsedEntry.is_recurring && recurringDesc && (
                <div className="p-3 bg-blue-900/30 border border-blue-700/50 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-400 mb-1">
                    <RotateCcw className="w-4 h-4" />
                    <span className="text-xs">Tekrar</span>
                  </div>
                  <p className="text-blue-300 text-sm">{recurringDesc}</p>
                </div>
              )}

              {/* Type */}
              <div className="p-3 bg-gray-800/50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-400 mb-1">
                  <Wallet className="w-4 h-4" />
                  <span className="text-xs">Tip</span>
                </div>
                <p className={`text-sm ${isIncome ? 'text-emerald-400' : 'text-red-400'}`}>
                  {isIncome ? 'Gelir' : 'Gider'}
                </p>
              </div>
            </div>
          </div>

          {/* Original command */}
          {parsedEntry.ai_parsed_from && (
            <div className="p-3 bg-gray-800/30 rounded-lg border border-gray-700/50">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <MessageSquare className="w-4 h-4" />
                <span className="text-xs">Orijinal komut</span>
              </div>
              <p className="text-sm text-gray-300 italic">"{parsedEntry.ai_parsed_from}"</p>
            </div>
          )}

          {/* Confidence indicator */}
          {parsedEntry.confidence && (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    parsedEntry.confidence > 0.8
                      ? 'bg-emerald-500'
                      : parsedEntry.confidence > 0.6
                      ? 'bg-amber-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${parsedEntry.confidence * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-500">
                %{Math.round(parsedEntry.confidence * 100)} güven
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-gray-800">
          <button
            onClick={editParsedEntry}
            className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Düzenle
          </button>
          <button
            onClick={closePreviewModal}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
          >
            İptal
          </button>
          <button
            onClick={confirmParsedEntry}
            className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            Onayla
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PreviewModal;
